import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { forLifeOf, LifecycleAware } from '@lsq-frontend/common-ui';
import { LsqListSource, SortDirectionEnterprise } from '@lsq-frontend/angular-shared/api-service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { AdminSuppliersBulkActionsService } from './bulk-actions.service';
import { ListRelationshipsForBulkActionRequest } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-bulk-actions/list-relationships-for-bulk-eligibility';
import { ListRelationshipsForBulkScfEnabledService } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-bulk-actions/list-relationships-for-bulk-scf-enabled';
import {
    BulkUpdateRelationshipScfEnabledRequest,
    BulkUpdateRelationshipScfEnabledService
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/operations/admin-supplier-bulk-actions/bulk-update-relationship-scf-enabled';
import {
    ListSupplierPaymentMethodsRequest,
    ListSupplierPaymentMethodsService,
    PaymentMethodStatus
} from '@lsq-frontend/remote-services/all-java/financing-mk1-services';
import { ListRelationshipsForBulkScfEnabledDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/list-relationships-for-bulk-scf-enabled-dto';
import { PaymentMethodStatusDisplay } from '../../models';

@LifecycleAware()
@Component({
    selector: 'admin-suppliers-bulk-enable-scf',
    template: `
        <bulk-action-layout
            [pageHeader]="(enable ? 'Enable' : 'Disable') + ' SCF'"
            [supplierCount]="(relationshipKeys$ | async).length"
            [confirmButtonText]="(enable ? 'Enable' : 'Disable') + ' SCF'"
            [disableConfirm]="showBankRequired || !checkableRowsService.checkedRows.length"
            (confirmClickHandler)="submitUpdate()"
        >
            <div supplier-table>
                <lsq-data-table [dataSource]="dataSource" [isPaginated]="true" [layoutFixed]="true">
                    <lsq-checkbox-column
                        [checkableRowsService]="checkableRowsService"
                        [column]="{
                            key: 'relationshipKey',
                            sortable: false,
                            width: '4rem'
                        }"
                    >
                    </lsq-checkbox-column>
                    <lsq-column
                        [column]="{
                            key: 'supplierName',
                            header: 'Supplier Name',
                            sortable: false,
                            truncateTextCss: true,
                            width: '20rem'
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            key: 'buyerName',
                            header: 'Buyer Relationship',
                            sortable: false,
                            truncateTextCss: true,
                            width: '20rem'
                        }"
                    >
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            key: 'scfEnabled',
                            header: 'Current Enable SCF',
                            sortable: false,
                            width: '10rem'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            {{ row.scfEnabled ? 'Enabled' : 'Disabled' }}
                        </ng-template>
                    </lsq-column>
                    <lsq-column
                        [column]="{
                            header: 'Bank Account',
                            key: 'defaultPaymentMethodStatus',
                            sortable: false,
                            width: '13%'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <div class="payment-method-column">
                                {{ getBankAccountVerified(row.supplierKey) ? 'VERIFIED' : 'NONE' }}
                            </div>
                        </ng-template>
                    </lsq-column>
                </lsq-data-table>
            </div>

            <div confirm-card>
                <p class="data-label">Update SCF</p>
                <p class="header" [style.marginBottom]="'2rem'">
                    {{ enable ? 'Enabled' : 'Disabled' }}
                </p>
                <p class="body" *ngIf="enable">
                    Once enabled, the Supplier will be able to request early payment and receive
                    at-term payments, and the Buyer will be able to request FlexTerm.
                </p>
                <p class="body" *ngIf="!enable">
                    Once disabled, the Supplier will not be able to request early payment or receive
                    at-term payments and the Buyer will not be able to request FlexTerm.
                </p>
            </div>
        </bulk-action-layout>
    `
})
export class AdminSuppliersBulkEnableScfComponent implements OnInit, OnDestroy {
    @Input() public enable: boolean;

    public relationshipKeys$ = this._adminSuppliersBulkActionsService.relationshipKeys$;

    public checkableRowsService = this._adminSuppliersBulkActionsService.checkableRowsService;

    public _skipSubject = new BehaviorSubject<number>(0);
    public _takeSubject = new BehaviorSubject<number>(10);
    public skip$ = this._skipSubject.asObservable();
    public take$ = this._takeSubject.asObservable();

    private _relationshipKeysToFetch$ = combineLatest([
        this.relationshipKeys$,
        this.skip$,
        this.take$
    ]).pipe(
        map(([relationshipKeys, skip, take]) =>
            !!relationshipKeys ? relationshipKeys.slice(skip, skip + take) : []
        )
    );

    // This is an unconventional data source, because we have not yet built out a backend that
    // keeps track of which rows we have checked, only the frontend knows. So we have to do manual
    // pagination by passing the relationshipKeys we want data for to the endpoint.
    public dataSource = new LsqListSource<ListRelationshipsForBulkScfEnabledDto>({
        data$: this._listRelationshipsForBulkScfEnabled.dataStream,
        rows$: this._listRelationshipsForBulkScfEnabled.dataStream.pipe(pluck('rows')),
        error$: this._listRelationshipsForBulkScfEnabled.errorStream,
        loading$: this._listRelationshipsForBulkScfEnabled.isInFlightStream,
        totalCount$: this.relationshipKeys$.pipe(map((relationships) => relationships?.length)),
        skip: 0,
        take: 10,
        handleSkipChange: (skip) => {
            this.dataSource.skip = skip;
            this._skipSubject.next(skip);
        },
        handleTakeChange: (take) => {
            this.dataSource.take = take;
            this._takeSubject.next(take);
        }
    });
    public showBankRequired: boolean;
    public suppliersCanEnabledScf: supplierBankVerified[] = [];
    public paymentMethodStatusDisplay = PaymentMethodStatusDisplay;

    constructor(
        private _adminSuppliersBulkActionsService: AdminSuppliersBulkActionsService,
        private _listRelationshipsForBulkScfEnabled: ListRelationshipsForBulkScfEnabledService,
        private _bulkUpdateRelationshipScfEnabled: BulkUpdateRelationshipScfEnabledService,
        private _listSupplierPaymentMethodsService: ListSupplierPaymentMethodsService
    ) {
        this._relationshipKeysToFetch$.pipe(forLifeOf(this)).subscribe((relationshipKeys) => {
            this._listRelationshipsForBulkScfEnabled.execute(
                new ListRelationshipsForBulkActionRequest({
                    relationshipKeys
                })
            );
            this.checkableRowsService.setCurrentPageKeys(relationshipKeys);
        });
    }
    public ngOnInit(): void {
        this.dataSource.rows$.pipe(forLifeOf(this)).subscribe(async (suppliers) => {
            for (const supplier of suppliers) {
                const account: boolean = await this.canEnabledScf(supplier.supplierKey);
                this.suppliersCanEnabledScf.push({
                    supplierKey: supplier.supplierKey,
                    bankAccountVerified: account
                });
            }
            if (this.enable) {
                this.showBankRequired = !this.suppliersCanEnabledScf.every(
                    (element) => element.bankAccountVerified === true
                );
            }
        });
    }
    public ngOnDestroy() {}

    public async canEnabledScf(supplierKey: string): Promise<boolean> {
        return !!(await this.hasBankAccountVerified(supplierKey));
    }

    private async hasBankAccountVerified(supplierKey: string): Promise<boolean> {
        const bankAccounts = await this._listSupplierPaymentMethodsService.executeOnce(
            new ListSupplierPaymentMethodsRequest({
                supplierKey: supplierKey,
                take: 100,
                skip: 0,
                sortColumn: 'default',
                sortDirection: SortDirectionEnterprise.Desc
            })
        );
        const bankAccount = bankAccounts?.rows[0];
        return (
            bankAccount?.default &&
            bankAccount?.paymentMethodStatus === PaymentMethodStatus.VERIFIED
        );
    }

    public getBankAccountVerified(supplierKey: string): boolean {
        const bankVerified = this.suppliersCanEnabledScf.find((supplierBankVerified) => {
            return supplierBankVerified.supplierKey === supplierKey;
        });
        return bankVerified?.bankAccountVerified;
    }

    public async submitUpdate(): Promise<void> {
        this._adminSuppliersBulkActionsService.setIsSubmitting(true);

        // Need to get the Supplier and Buyer names of all checked rows,
        // since we only currently have the ones for the current page of the table.
        const checkedRelationships = (
            await this._listRelationshipsForBulkScfEnabled.executeOnce(
                new ListRelationshipsForBulkActionRequest({
                    relationshipKeys: this.checkableRowsService.checkedRows as string[]
                })
            )
        ).rows;

        try {
            await this._bulkUpdateRelationshipScfEnabled.executeOnce(
                new BulkUpdateRelationshipScfEnabledRequest({
                    relationshipKeys: checkedRelationships.map((rel) => rel.relationshipKey),
                    scfEnabled: this.enable
                })
            );

            this._adminSuppliersBulkActionsService.showConfirmationModal(
                `Supplier SCF ${this.enable ? 'Enabled' : 'Disabled'}`,
                this.enable
                    ? 'The following suppliers have now enabled SCF:'
                    : 'The following suppliers have now been disabled for SCF',
                checkedRelationships
            );
        } catch (error) {
            console.log(error);
        }
        this._adminSuppliersBulkActionsService.setIsSubmitting(false);
    }
}

interface supplierBankVerified {
    supplierKey: string;
    bankAccountVerified: boolean;
}
